<template>

    <BlockingModal ref="cookieModal" title="Cookie Notice">
        <p class="mb-4">
            We use cookies to analyse traffic and improve our website. You can accept or decline cookies below.
            For more information, see our <NuxtLink class="underline text-sn-dark-blue" to="/privacy-and-cookies">privacy and cookie policy</NuxtLink>
        </p>
        <div class="flex gap-4">
            <Button @click="refuseCookies" type="primary" colour="blue" size="large">Decline</Button>
            <Button @click="acceptCookies" type="primary" colour="blue" size="large">Accept</Button>
        </div>
    </BlockingModal>

</template>

<script setup lang="ts">

    import Button from "@/components/core/Button.vue";
    import {useApi} from "@/composables/useApi";
    import Modal from "@/components/core/modals/Modal.vue";
    import type {ComponentExposed} from "vue-component-type-helpers";
    import BlockingModal from "~/components/core/modals/BlockingModal.vue";

    const cookieModal = ref<ComponentExposed<typeof Modal>|null>(null);

    const config = useRuntimeConfig();

    const cookiesConsent = useCookie<undefined|string>('cookie_consent', {
        maxAge: 60 * 60 * 24 * 365, // 1 year
        sameSite: 'lax',
        secure: true,
        path: '/',
        default: () => undefined,
    });

    onMounted(async () => {
        if (cookiesConsent.value === undefined) {
            cookieModal.value?.open();
        } else if (String(cookiesConsent.value) === '1') {
            setupGoogleAnalytics();
        }
    });

    function acceptCookies() {
        cookiesConsent.value = '1';
        setupGoogleAnalytics();
        cookieModal.value?.close();
        storeCookieResponse(true);
    }

    function refuseCookies() {
        cookiesConsent.value = '0';
        cookieModal.value?.close();
        storeCookieResponse(false);
    }

    async function storeCookieResponse(status: boolean, type = 'ANALYTICS') {
        const { data, fetch } = useApi({
            url: 'cookie-responses',
            method: 'POST',
            payload: {
                'category': type,
                'consent_status': status,
            }
        });
        await fetch();
    }

    function setupGoogleAnalytics() {
        useHead({
            script: [
                {
                    src: `https://www.googletagmanager.com/gtag/js?id=${config.public.googleAnalyticsId}`,
                    async: true,
                },
                {
                    innerHTML: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${config.public.googleAnalyticsId}');
                    `
                }
            ]
        });
    }


</script>